import { useMemo } from "react";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { useServerTable } from "../../../components/table/tableFeatures";
import { MdRotateLeft } from "react-icons/md";
import { HiExternalLink } from "react-icons/hi";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { resetTable, idsToVisibilityState } from "../../../components/table/utils";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { useStandardEntityMetrics } from "../../merchandising/components/tables/metricUtils";
import { Button } from "../../../components/core/basic/Button";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { useGetDSPInventoryPaginated } from "../hooks/useDSPInventoryPaginatedQuery";
import type { DSPInventoryRow } from "../../../zodios/apiDefinitions/api/dsp/inventoryDatasets";
import InventoryExpander from "./InventoryExpander";

const columnHelper = createColumnBuilder<DSPInventoryRow>();

const defaultSelectedColumns = [
  "dsp_sales",
  "dsp_total_sales",
  "dsp_ecpm",
  "dsp_impressions",
  "dsp_clicks",
  "dsp_cost",
  "dsp_total_purchases",
  "dsp_units",
  "dsp_ctr",
];

type Props = {
  adGroupId: string | number;
};

const DSPInventoryTable = ({ adGroupId }: Props) => {
  const { t } = useTranslation(["dsp", "common"]);
  const standardMetricIds = useStandardEntityMetrics("dspInventory");

  const defaultColumnVisibility = useMemo(
    () => idsToVisibilityState(["site", ...defaultSelectedColumns], ["site", ...standardMetricIds]),
    [standardMetricIds],
  );

  const { state, initialState, setters } = usePersistedTableState({
    persistKey: "dspInventoryTable",
    initialState: {
      columnVisibility: defaultColumnVisibility,
      sorting: [{ id: "dsp_total_sales", desc: true }],
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor("site", {
        id: "site",
        header: t(($) => $.dsp["Site"]),
        minSize: 200,
        size: 300,
        maxSize: 500,
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const site = getValue();
          return (
            <span className={css({ fontWeight: "medium" })}>
              <img
                src={`https://www.google.com/s2/favicons?domain=${site}`}
                alt={site ?? ""}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />{" "}
              {site}{" "}
              <a
                href={`https://${site}`}
                target="_blank"
                rel="noopener noreferrer"
                title={t(($) => $.dsp["View Site in New Tab"])}
              >
                <HiExternalLink />
              </a>
            </span>
          );
        },
        meta: {
          columnSelectorOptions: { label: t(($) => $.dsp["Site"]) },
        },
      }),
      ...standardMetricIds.map((id) => columnHelper.metric(id)),
    ],
    [standardMetricIds, t],
  );

  const debouncedSearch = useDebounce(state.globalFilter, 500);

  const getQueryOptions = useGetDSPInventoryPaginated({ lineItemId: adGroupId });
  const inventoryQuery = useBlockWindowQuery(getQueryOptions, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters: state.columnFilters,
    search: debouncedSearch,
  });

  const tableData = useMemo<DSPInventoryRow[]>(() => inventoryQuery.data?.data ?? [], [inventoryQuery.data]);

  const table = useServerTable({
    data: tableData,
    columns,
    initialState,
    state,
    ...setters,
    rowCount: inventoryQuery.data?.count ?? undefined,
    getRowId: (row) => row.site ?? "",
    getRowCanExpand: () => true,
    meta: {
      showSkeleton: inventoryQuery.isFetching && (!inventoryQuery.data || inventoryQuery.isPlaceholderData),
    },
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions.Search)}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={(row) => <InventoryExpander row={row} lineItemId={adGroupId} />}
      />
    </>
  );
};

export default DSPInventoryTable;
